import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import HeroSection from "./components/HeroSection";
import SkillsPreview from "./components/SkillsPreview";
import FeaturedProjects from "./components/FeaturedProjects";
import ExperienceSummary from "./components/ExperienceSummary";
import SocialLinks from "./components/SocialLinks";
import CallToAction from "./components/CallToAction";

const HomeLandingPage = () => {
  useEffect(() => {
    // Smooth scroll behavior for anchor links
    const handleSmoothScroll = (e) => {
      const target = e?.target?.closest('a[href^="#"]');
      if (target) {
        e?.preventDefault();
        const targetId = target?.getAttribute("href")?.substring(1);
        const element = document.getElementById(targetId);
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    document.addEventListener("click", handleSmoothScroll);
    return () => document.removeEventListener("click", handleSmoothScroll);
  }, []);

  useEffect(() => {
    // Add loading animation classes
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll("section");
    sections?.forEach((section) => observer?.observe(section));

    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>
          有木 祥子 のポートフォリオ - FileMaker Developer | Portfolio
        </title>
        <meta
          name="description"
          content="Experienced full-stack developer and UI/UX designer specializing in React, Node.js, and modern web technologies. View my portfolio of 50+ completed projects and 5+ years of professional experience."
        />
        <meta
          name="keywords"
          content="full-stack developer, React developer, UI/UX designer, web development, JavaScript, Node.js, portfolio"
        />
        <meta name="author" content="Sarah Johnson" />
        <meta
          property="og:title"
          content="Sarah Johnson - Full-Stack Developer & UI/UX Designer"
        />
        <meta
          property="og:description"
          content="Professional portfolio showcasing full-stack development and UI/UX design expertise with 50+ completed projects."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sarahjohnson.dev" />
        <meta
          property="og:image"
          content="https://sarahjohnson.dev/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@sarahdev" />
        <link rel="canonical" href="https://sarahjohnson.dev" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Sarah Johnson",
            jobTitle: "Full-Stack Developer & UI/UX Designer",
            description:
              "Experienced full-stack developer specializing in React, Node.js, and modern web technologies",
            url: "https://sarahjohnson.dev",
            sameAs: [
              "https://github.com/sarahjohnson",
              "https://linkedin.com/in/sarahjohnson",
              "https://twitter.com/sarahdev",
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <HeroSection />

        {/* Skills Preview Section */}
        <SkillsPreview />

        {/* Featured Projects Section */}
        <FeaturedProjects />

        {/* Experience Summary Section */}
        <ExperienceSummary />

        {/* Social Links Section */}
        {/* <SocialLinks /> */}

        {/* Call to Action Section */}
        <CallToAction />

        {/* Back to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-elevation-lg hover:shadow-elevation-md transition-layout hover-lift flex items-center justify-center z-50"
          aria-label="Back to top"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      </div>
    </>
  );
};

export default HomeLandingPage;
